import { Controller } from "@hotwired/stimulus"

// Drives the shared "Save to your game" dialog. Each card's Save button
// (data-action="area-picker#open") points at the picker path; we load it into
// the frame and open the native <dialog>. The picker form closes the dialog on
// a successful submit (turbo:submit-end).
export default class extends Controller {
  static targets = ["dialog", "frame"]

  open(event) {
    event.preventDefault()
    // Force a fresh load every open so the checkboxes reflect current state,
    // even when re-opening the same video.
    this.frameTarget.src = event.currentTarget.href
    if (this.dialogTarget.open) return

    if (this.closeTimer) { clearTimeout(this.closeTimer); this.closeTimer = null }
    this.dialogTarget.showModal()
    // On mobile the dialog is a full-height sheet; slide it up from the bottom.
    // Reveal it off-screen (showModal), force a reflow so the browser paints
    // that start frame, then flip `is-open` so the transform animates. The
    // reflow is skipped when there's no animation (desktop / reduced motion) to
    // avoid a one-frame flash of the off-screen start state.
    if (this.animatedSheet) void this.dialogTarget.offsetWidth
    this.dialogTarget.classList.add("is-open")
  }

  close() {
    if (!this.dialogTarget.open) return

    // Desktop (centred dialog) and reduced-motion close instantly. On the mobile
    // sheet, slide back down first, then defer the native close() — it flips to
    // display:none immediately and would otherwise cut off the slide-out.
    if (!this.animatedSheet) { this.dialogTarget.close(); return }

    this.dialogTarget.classList.remove("is-open")
    clearTimeout(this.closeTimer)
    this.closeTimer = setTimeout(() => {
      if (this.dialogTarget.open) this.dialogTarget.close()
    }, 150)
  }

  // The bottom-sheet slide only exists on the mobile breakpoint (see
  // area_picker.tailwind.css) and is suppressed under reduced-motion.
  get animatedSheet() {
    return window.matchMedia("(max-width: 640px)").matches &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  onSubmitEnd(event) {
    if (event.detail.success) this.close()
  }

  // Native <dialog> click covers the whole viewport; treat a click on the
  // backdrop (outside the panel) as a request to close.
  backdropClose(event) {
    if (event.target === this.dialogTarget) this.close()
  }

  // Drop the src on close so re-opening the same video always re-fetches fresh
  // checkbox state rather than reusing the already-loaded frame. Also clear the
  // slide state so however the dialog closed (animated close, Escape, submit) the
  // next open starts from the bottom again.
  onClose() {
    this.dialogTarget.classList.remove("is-open")
    this.frameTarget.removeAttribute("src")
  }
}
